import React, { useEffect, useMemo } from "react";
import { useAppStore } from "../store/useAppStore";

const formatNumber = (value) => {
  if (value == null || Number.isNaN(value)) return "—";
  return new Intl.NumberFormat("ru-RU").format(value);
};

const formatRating = (value) => {
  if (value == null || Number.isNaN(value)) return "—";
  return value.toFixed(1);
};

const MainPage = () => {
  const dashboardStats = useAppStore((state) => state.dashboardStats);
  const fetchDashboardStats = useAppStore((state) => state.fetchDashboardStats);
  const setActivePage = useAppStore((state) => state.setActivePage);

  useEffect(() => {
    fetchDashboardStats();
  }, [fetchDashboardStats]);

  const { requests, signs, sentences } = dashboardStats;
  const translationPercent = sentences.total
    ? Math.round((sentences.translated / sentences.total) * 100)
    : 0;

  const actionItems = useMemo(() => {
    const items = [];

    if (requests.new > 0) {
      items.push({
        key: "new-requests",
        icon: "fa-clipboard-list",
        tone: "warning",
        label: `${formatNumber(requests.new)} новых заявок`,
        hint: "Ожидают обработки",
        page: "requests",
      });
    }

    if (signs.withoutVideo > 0) {
      items.push({
        key: "signs-without-video",
        icon: "fa-video-slash",
        tone: "info",
        label: `${formatNumber(signs.withoutVideo)} жестов без видео`,
        hint: "Нужно загрузить медиа",
        page: "gestures",
      });
    }

    if (sentences.untranslated > 0) {
      items.push({
        key: "untranslated-sentences",
        icon: "fa-language",
        tone: "accent",
        label: `${formatNumber(sentences.untranslated)} предложений без перевода`,
        hint: "Можно взять в работу",
        page: "translation",
      });
    }

    return items;
  }, [requests.new, signs.withoutVideo, sentences.untranslated]);

  const kpiCards = [
    {
      key: "new-requests",
      label: "Новые заявки",
      value: formatNumber(requests.new),
      icon: "fa-bell",
      tone: "warning",
      page: "requests",
    },
    {
      key: "total-requests",
      label: "Всего заявок",
      value: formatNumber(requests.total),
      icon: "fa-clipboard-list",
      tone: "default",
      page: "requests",
    },
    {
      key: "signs",
      label: "Жестов в базе",
      value: formatNumber(signs.total),
      icon: "fa-hands",
      tone: "default",
      page: "gestures",
    },
    {
      key: "sentences",
      label: "Предложений",
      value: formatNumber(sentences.total),
      icon: "fa-quote-right",
      tone: "default",
      page: "sentences",
    },
    {
      key: "untranslated",
      label: "Без перевода",
      value: formatNumber(sentences.untranslated),
      icon: "fa-language",
      tone: "accent",
      page: "translation",
    },
    {
      key: "avg-rating",
      label: "Средний рейтинг",
      value: formatRating(requests.avgRating),
      icon: "fa-star",
      tone: "success",
      page: "requests",
    },
  ];

  return (
    <main className="main-page">
      <div className="main-card">
        <div className="requests-head dashboard-head">
          <h1>
            <i className="fa-solid fa-house" aria-hidden="true"></i> Главная
          </h1>
          <div className="requests-head-actions">
            <button className="e-btn" type="button" onClick={fetchDashboardStats} disabled={dashboardStats.loading}>
              <i className={`fa-solid fa-rotate-right${dashboardStats.loading ? " fa-spin" : ""}`} aria-hidden="true"></i>
              Обновить
            </button>
          </div>
        </div>

        {dashboardStats.loading ? (
          <p className="dashboard-status">
            <i className="fa-solid fa-spinner fa-spin" aria-hidden="true"></i> Загрузка статистики...
          </p>
        ) : null}

        {dashboardStats.error ? (
          <p className="requests-error">
            <i className="fa-solid fa-circle-exclamation" aria-hidden="true"></i> {dashboardStats.error}
          </p>
        ) : null}

        {!dashboardStats.loading && !dashboardStats.error ? (
          <>
            <div className="dashboard-kpi-grid">
              {kpiCards.map((card) => (
                <button
                  key={card.key}
                  type="button"
                  className={`dashboard-kpi-card dashboard-kpi-card--${card.tone}`}
                  onClick={() => setActivePage(card.page)}
                >
                  <div className="dashboard-kpi-icon">
                    <i className={`fa-solid ${card.icon}`} aria-hidden="true"></i>
                  </div>
                  <div className="dashboard-kpi-body">
                    <span className="dashboard-kpi-value">{card.value}</span>
                    <span className="dashboard-kpi-label">{card.label}</span>
                  </div>
                </button>
              ))}
            </div>

            <div className="dashboard-panels">
              <section className="dashboard-panel">
                <h2>
                  <i className="fa-solid fa-list-check" aria-hidden="true"></i> Требует действия
                </h2>
                {actionItems.length ? (
                  <ul className="dashboard-action-list">
                    {actionItems.map((item) => (
                      <li key={item.key}>
                        <button
                          type="button"
                          className={`dashboard-action-item dashboard-action-item--${item.tone}`}
                          onClick={() => setActivePage(item.page)}
                        >
                          <span className="dashboard-action-icon">
                            <i className={`fa-solid ${item.icon}`} aria-hidden="true"></i>
                          </span>
                          <span className="dashboard-action-text">
                            <strong>{item.label}</strong>
                            <span>{item.hint}</span>
                          </span>
                          <i className="fa-solid fa-chevron-right dashboard-action-arrow" aria-hidden="true"></i>
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="dashboard-empty">
                    <i className="fa-solid fa-circle-check" aria-hidden="true"></i> Все задачи закрыты — отличная работа!
                  </p>
                )}
              </section>

              <section className="dashboard-panel">
                <h2>
                  <i className="fa-solid fa-chart-simple" aria-hidden="true"></i> Перевод предложений
                </h2>
                <div className="dashboard-progress">
                  <div className="dashboard-progress-head">
                    <span>Переведено</span>
                    <strong>
                      {formatNumber(sentences.translated)} / {formatNumber(sentences.total)} ({translationPercent}%)
                    </strong>
                  </div>
                  <div className="dashboard-progress-track" role="progressbar" aria-valuenow={translationPercent} aria-valuemin={0} aria-valuemax={100}>
                    <div className="dashboard-progress-fill" style={{ width: `${translationPercent}%` }} />
                  </div>
                  {sentences.untranslated > 0 ? (
                    <button className="e-btn dashboard-progress-btn" type="button" onClick={() => setActivePage("translation")}>
                      Перейти к переводу
                    </button>
                  ) : null}
                </div>
              </section>

              <section className="dashboard-panel dashboard-panel--wide">
                <h2>
                  <i className="fa-solid fa-star" aria-hidden="true"></i> Топ заявок по рейтингу
                </h2>
                {requests.topByRating.length ? (
                  <div className="dashboard-top-table-wrap">
                    <table className="dashboard-top-table">
                      <thead>
                        <tr>
                          <th>Lemma</th>
                          <th>Рейтинг</th>
                          <th>Статус</th>
                        </tr>
                      </thead>
                      <tbody>
                        {requests.topByRating.map((item) => (
                          <tr key={item.id}>
                            <td>{item.lemma || "—"}</td>
                            <td className="dashboard-top-rating">{item.rating}</td>
                            <td>
                              <span className={`status-badge ${item.added ? "added" : "new"}`}>
                                {item.added ? "Добавлено" : "Новая"}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="dashboard-empty">
                    <i className="fa-regular fa-star" aria-hidden="true"></i> Пока нет заявок с рейтингом
                  </p>
                )}
              </section>
            </div>
          </>
        ) : null}
      </div>
    </main>
  );
};

export default MainPage;
